/**
 * 主题切换 Hook
 * 支持 system / light / dark / geek / hlight / hdark
 */
import { useEffect, useMemo, useState } from 'react'
import { useThemeStore, type ThemeMode } from '@/store/themeStore'

export type ResolvedTheme = 'light' | 'dark'

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export const useTheme = () => {
  const hydrated = useThemeStore((state) => state.hydrated)
  const themeMode = useThemeStore((state) => state.themeMode)
  const setThemeMode = useThemeStore((state) => state.setThemeMode)
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme)

  useEffect(() => {
    if (!window.matchMedia) return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light')
    }

    if (media.addEventListener) {
      media.addEventListener('change', handler)
    } else {
      // Safari < 14
      media.addListener(handler)
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handler)
      } else {
        media.removeListener(handler)
      }
    }
  }, [])

  const resolvedTheme = useMemo<ResolvedTheme>(() => {
    if (themeMode === 'system') return systemTheme
    if (themeMode === 'geek') return 'dark'
    if (themeMode === 'hdark') return 'dark'
    if (themeMode === 'hlight') return 'light'
    if (themeMode === 'cyber') return 'dark'
    if (themeMode === 'tech') return 'dark'
    if (themeMode === 'blue') return 'dark'
    return themeMode
  }, [themeMode, systemTheme])

  return {
    hydrated,
    themeMode,
    setThemeMode,
    resolvedTheme,
    isDark: resolvedTheme === 'dark',
    isGeek: themeMode === 'geek',
    isHero: themeMode === 'hlight' || themeMode === 'hdark',
    isCyber: themeMode === 'cyber',
    isTech: themeMode === 'tech',
    isBlue: themeMode === 'blue',
  }
}

export type { ThemeMode }
